import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaPen, FaPlusCircle, FaSearch, FaUsers } from "react-icons/fa";
import { toast } from "react-toastify";
import api from "../api/httpClient";
import { API_PATHS } from "../api/apiPaths";
import { getCachedOrFetch, getTenantCacheKey, invalidateCache } from "../utils/requestCache";
import { useAppTheme } from "../context/AppThemeContext";
import AdminResponsiveTabs from "./AdminResponsiveTabs";
import AdminModalShell from "./AdminModalShell";
import AdminMobileViewToggle from "./AdminMobileViewToggle";
import ModuleImportButton from "./ModuleImportButton";
import ModuleRefreshButton from "./ModuleRefreshButton";

const emptyForm = {
  id: "",
  parentName: "",
  email: "",
  mobile: "",
  studentName: "",
  studentId: "",
  studentSelection: "",
  className: "",
  portalStatus: "INVITED",
  communicationPreference: "Email",
};

const normalizeParent = (record = {}, index = 0) => ({
  id: record.id || record.parentId || record._id || `parent-${index + 1}`,
  parentName: record.parentName || record.name || `Parent ${index + 1}`,
  email: record.email || "",
  mobile: record.mobile || record.phone || "",
  studentName: record.studentName || "",
  className: record.className || record.standard || "",
  portalStatus: String(record.portalStatus || record.status || "INVITED").toUpperCase(),
  communicationPreference: record.communicationPreference || record.preference || "Email",
});

const extractParents = (responseData) => {
  const payload = responseData?.data ?? responseData ?? [];
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.parents)) return payload.parents;
  if (Array.isArray(payload?.content)) return payload.content;
  return [];
};

const badgeTone = (status) => {
  if (status === "ACTIVE") return "bg-emerald-100 text-emerald-700";
  if (status === "INVITED") return "bg-sky-100 text-sky-700";
  return "bg-rose-100 text-rose-700";
};

export default function AdminParentPortal({ createMode = false }) {
  const { isDarkMode } = useAppTheme();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("table");
  const [parents, setParents] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedId, setSelectedId] = useState("");
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [mobileRecordView, setMobileRecordView] = useState("cards");
  const [form, setForm] = useState(emptyForm);
  const [refreshVersion, setRefreshVersion] = useState(0);

  useEffect(() => {
    const fetchParents = async () => {
      setLoading(true);
      try {
        const response = await getCachedOrFetch(getTenantCacheKey("admin:parents"), () => api.get(API_PATHS.admin.parents()), { forceRefresh: refreshVersion > 0 });
        const nextRecords = extractParents(response.data).map(normalizeParent);
        if (!createMode && nextRecords.length > 0) {
          setParents(nextRecords);
          setSelectedId(nextRecords[0].id);
        } else if (nextRecords.length > 0) {
          setParents(nextRecords);
          setSelectedId("");
        } else {
          setParents([]);
          setSelectedId("");
        }
      } catch {
        setParents([]);
        setSelectedId("");
      } finally {
        setLoading(false);
      }
    };
    fetchParents();
  }, [refreshVersion, createMode]);

  useEffect(() => {
    const fetchStudents = async () => {
      try {
        const response = await getCachedOrFetch(getTenantCacheKey("admin:students"), () => api.get(API_PATHS.admin.students()));
        const payload = response.data?.data ?? response.data ?? [];
        const records = Array.isArray(payload) ? payload : payload.students || payload.content || [];
        setStudents(records.map((student) => {
          const name = student.name || student.fullName || student.studentName || [student.firstName, student.lastName].filter(Boolean).join(" ") || "Unnamed Student";
          const className = student.className || student.standard || "";
          const section = student.section || "";
          return { id: student.id || student.studentId, name, className: `${className}${section ? ` - ${section}` : ""}`, label: `${name}${className ? ` — ${className}${section ? ` ${section}` : ""}` : ""}` };
        }));
      } catch { setStudents([]); }
    };
    fetchStudents();
  }, []);

  useEffect(() => { if (createMode) { setSelectedId(""); setForm({ ...emptyForm }); setIsFormOpen(true); } }, [createMode]);

  const selectedParent = useMemo(() => (
    parents.find((item) => item.id === selectedId) || null
  ), [parents, selectedId]);

  useEffect(() => {
    if (createMode || !selectedId || !selectedParent) {
      setForm({ ...emptyForm });
      return;
    }
    setForm(selectedParent);
  }, [selectedParent, selectedId, createMode]);

  const filteredParents = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    return parents.filter((item) => (
      !query
      || item.parentName.toLowerCase().includes(query)
      || item.studentName.toLowerCase().includes(query)
      || item.email.toLowerCase().includes(query)
      || item.className.toLowerCase().includes(query)
    ));
  }, [parents, searchTerm]);

  const summary = useMemo(() => ({
    total: parents.length,
    active: parents.filter((item) => item.portalStatus === "ACTIVE").length,
    invited: parents.filter((item) => item.portalStatus === "INVITED").length,
    locked: parents.filter((item) => item.portalStatus === "LOCKED").length,
  }), [parents]);

  const updateForm = (key, value) => setForm((current) => ({ ...current, [key]: value }));
  const resetForm = () => {
    setSelectedId("");
    setForm(emptyForm);
  };
  const openCreateForm = () => {
    setSelectedId("");
    setForm({ ...emptyForm });
    setIsFormOpen(true);
    if (!createMode) {
      navigate("/admin/parent-portal/create");
    }
  };
  const openEditForm = (id) => {
    const target = parents.find((item) => item.id === id);
    setSelectedId(id);
    if (target) {
      setForm(target);
    }
    setIsFormOpen(true);
  };

  const closeForm = () => {
    setSelectedId("");
    setForm({ ...emptyForm });
    setIsFormOpen(false);
    if (createMode) {
      navigate("/admin/parent-portal");
    }
  };

  const upsertParent = (nextRecord) => {
    setParents((current) => {
      const exists = current.some((item) => item.id === nextRecord.id);
      if (!exists) return [nextRecord, ...current];
      return current.map((item) => (item.id === nextRecord.id ? nextRecord : item));
    });
    setSelectedId(nextRecord.id);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);

    const payload = {
      parentName: form.parentName.trim(),
      email: form.email.trim(),
      mobile: form.mobile.trim(),
      studentId: form.studentId || undefined,
      studentName: form.studentName.trim(),
      className: form.className.trim(),
      portalStatus: form.portalStatus,
      communicationPreference: form.communicationPreference,
    };
    const isEditing = Boolean(form.id);

    try {
      const response = isEditing
        ? await api.put(API_PATHS.admin.parentById(form.id), payload)
        : await api.post(API_PATHS.admin.parents(), payload);

      const nextRecord = normalizeParent(
        { ...(response.data?.data ?? response.data ?? {}), ...payload, id: isEditing ? form.id : undefined },
        parents.length
      );
      upsertParent(nextRecord);
      invalidateCache(getTenantCacheKey("admin:parents"));
      setSelectedId("");
      setForm({ ...emptyForm });
      setIsFormOpen(false);
      if (createMode) navigate("/admin/parent-portal");
      toast.success(isEditing ? "Parent record updated." : "Parent record created.");
    } catch {
      toast.error("Unable to save parent record. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={`space-y-4 ${isDarkMode ? "text-slate-100" : "text-slate-900"}`}>
      <AdminResponsiveTabs tabs={[{ key: "table", label: "Parents" }, { key: "summary", label: "Summary" }]} activeKey={activeTab} onChange={setActiveTab} tone="amber" isDarkMode={isDarkMode} />

      <div className="border-x border-b border-slate-200 bg-white p-6 shadow-lg shadow-slate-200/60">
        {activeTab === "table" && (
          <>
            <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">Parent Portal</p>
                <h2 className="mt-1 text-2xl font-bold text-slate-900">Manage linked guardians</h2>
              </div>
              <div className="flex w-full gap-2 md:w-auto">
                <div className="relative w-full md:w-72">
                  <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(event) => setSearchTerm(event.target.value)}
                    className="w-full rounded-xl border border-slate-300 py-2.5 pl-10 pr-3 text-sm focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-100"
                    placeholder="Search parent, student, class"
                  />
                </div>
                <ModuleRefreshButton onRefresh={() => setRefreshVersion((value) => value + 1)} />
                <ModuleImportButton moduleKey="parents" label="Import parents" />
                <button
                  type="button"
                  onClick={openCreateForm}
                  className="inline-flex items-center gap-2 rounded-xl bg-amber-500 px-4 py-2 text-sm font-semibold text-white hover:bg-amber-600"
                >
                  <FaPlusCircle />
                  Add
                </button>
              </div>
            </div>

            <div className="mb-3"><AdminMobileViewToggle value={mobileRecordView} onChange={setMobileRecordView} isDarkMode={isDarkMode} /></div>
            {mobileRecordView === "cards" && (
              <div className="space-y-3 md:hidden">
                {loading ? <p className="rounded-2xl bg-slate-50 p-6 text-center text-sm text-slate-500">Loading parent records...</p> : filteredParents.length === 0 ? <p className="rounded-2xl bg-slate-50 p-6 text-center text-sm text-slate-500">No parent records found.</p> : filteredParents.map((item) => (
                  <article key={item.id} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                    <div className="flex items-start justify-between gap-3"><div><h3 className="font-bold text-slate-900">{item.parentName}</h3><p className="mt-1 text-xs text-slate-500">{item.studentName || "No linked student"}{item.className ? ` · ${item.className}` : ""}</p></div><span className={`rounded-full px-2 py-1 text-[10px] font-semibold ${badgeTone(item.portalStatus)}`}>{item.portalStatus}</span></div>
                    <div className="mt-3 rounded-xl bg-slate-50 p-3 text-xs text-slate-600"><p>{item.email || "No email"}</p><p className="mt-1">{item.mobile || "No mobile"} · {item.communicationPreference}</p></div>
                    <button type="button" onClick={() => openEditForm(item.id)} className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-xl border border-slate-300 px-3 py-2 text-xs font-bold text-slate-700"><FaPen /> Update parent</button>
                  </article>
                ))}
              </div>
            )}

            <div className={`${mobileRecordView === "cards" ? "hidden md:block" : "block"} overflow-hidden rounded-2xl border border-slate-200`}>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-slate-100 text-slate-600">
                    <tr>
                      <th className="px-4 py-3 text-left font-semibold">Parent Name</th>
                      <th className="px-4 py-3 text-left font-semibold">Student</th>
                      <th className="px-4 py-3 text-left font-semibold">Class</th>
                      <th className="px-4 py-3 text-left font-semibold">Email</th>
                      <th className="px-4 py-3 text-left font-semibold">Mobile</th>
                      <th className="px-4 py-3 text-left font-semibold">Preference</th>
                      <th className="px-4 py-3 text-left font-semibold">Status</th>
                      <th className="px-4 py-3 text-left font-semibold">Action</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white text-slate-800">
                    {loading ? (
                      <tr>
                        <td colSpan="8" className="px-4 py-8 text-center text-slate-500">Loading parent records...</td>
                      </tr>
                    ) : filteredParents.length === 0 ? (
                      <tr>
                        <td colSpan="8" className="px-4 py-8 text-center text-slate-500">No parent records found.</td>
                      </tr>
                    ) : filteredParents.map((item) => (
                      <tr key={item.id} className="border-t border-slate-200 hover:bg-slate-50">
                        <td className="px-4 py-3 font-semibold">{item.parentName}</td>
                        <td className="px-4 py-3">{item.studentName}</td>
                        <td className="px-4 py-3">{item.className}</td>
                        <td className="px-4 py-3">{item.email}</td>
                        <td className="px-4 py-3">{item.mobile}</td>
                        <td className="px-4 py-3">{item.communicationPreference}</td>
                        <td className="px-4 py-3">
                          <span className={`rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] ${badgeTone(item.portalStatus)}`}>
                            {item.portalStatus}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <button
                            type="button"
                            onClick={() => openEditForm(item.id)}
                            className="inline-flex items-center gap-1 rounded-lg border border-slate-300 px-2.5 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-100"
                          >
                            <FaPen />
                            Edit
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        {activeTab === "summary" && (
          <div className="space-y-4">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">Summary</p>
              <h2 className="mt-1 text-2xl font-bold text-slate-900">Portal status overview</h2>
            </div>
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
              {[
                { label: "Total", value: summary.total },
                { label: "Active", value: summary.active },
                { label: "Invited", value: summary.invited },
                { label: "Locked", value: summary.locked },
              ].map((item) => (
                <div key={item.label} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <div className="flex items-center justify-between">
                    <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">{item.label}</p>
                    <FaUsers className="text-slate-400" />
                  </div>
                  <p className="mt-2 text-2xl font-bold text-slate-900">{item.value}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <AdminModalShell isOpen={isFormOpen || createMode} onClose={closeForm} isDarkMode={isDarkMode} maxWidth="max-w-3xl" pageMode={createMode}>
        <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-lg">
          <h3 className="text-xl font-bold text-slate-900">{form.id ? "Update Parent Link" : "Create Parent Link"}</h3>
          <form onSubmit={handleSubmit} className="mt-5 space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className="mb-1 block text-sm font-medium text-slate-700">Parent name</label>
                <input type="text" value={form.parentName} onChange={(event) => updateForm("parentName", event.target.value)} className="w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm" required />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-slate-700">Email</label>
                <input type="email" value={form.email} onChange={(event) => updateForm("email", event.target.value)} className="w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm" required />
              </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className="mb-1 block text-sm font-medium text-slate-700">Mobile</label>
                <input type="text" value={form.mobile} onChange={(event) => updateForm("mobile", event.target.value)} className="w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm" required />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-slate-700">Student name</label>
                <input list="parent-student-options" type="text" value={form.studentSelection || form.studentName} onChange={(event) => { const value = event.target.value; const student = students.find((item) => item.label === value); setForm((current) => ({ ...current, studentSelection: value, studentId: student?.id || "", studentName: student?.name || value, className: student?.className || (student ? "" : current.className) })); }} className="w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm" placeholder="Select an existing student or enter a new name" required />
                <datalist id="parent-student-options">{students.map((student) => <option key={student.id} value={student.label} />)}</datalist>
                <p className="mt-1 text-xs text-slate-500">Choose an existing student to populate the class, or type a new student name.</p>
              </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-3">
              <div>
                <label className="mb-1 block text-sm font-medium text-slate-700">Class</label>
                <input type="text" value={form.className} onChange={(event) => updateForm("className", event.target.value)} className="w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm" required />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-slate-700">Portal status</label>
                <select value={form.portalStatus} onChange={(event) => updateForm("portalStatus", event.target.value)} className="w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm">
                  <option value="ACTIVE">Active</option>
                  <option value="INVITED">Invited</option>
                  <option value="LOCKED">Locked</option>
                </select>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-slate-700">Communication</label>
                <select value={form.communicationPreference} onChange={(event) => updateForm("communicationPreference", event.target.value)} className="w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm">
                  <option value="Email">Email</option>
                  <option value="WhatsApp">WhatsApp</option>
                  <option value="SMS">SMS</option>
                  <option value="In-app">In-app</option>
                </select>
              </div>
            </div>

            <div className="flex flex-wrap gap-3">
              <button type="submit" disabled={saving} className="rounded-xl bg-amber-500 px-4 py-2 text-sm font-semibold text-white hover:bg-amber-600 disabled:opacity-70">
                {saving ? "Saving..." : form.id ? "Update" : "Create"}
              </button>
              <button type="button" onClick={resetForm} className="rounded-xl border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-100">
                Reset
              </button>
            </div>
          </form>
        </div>
      </AdminModalShell>
    </div>
  );
}
